<template>
  <v-card>
    <v-card-title class="text-h5 align-center bb-1">
      <v-icon class="pr-2" color="secondary">mdi-translate</v-icon>
      {{ $t('Marketing Content Translations') }}
      <v-spacer />
      <v-btn icon @click="onClose">
        <v-icon color="primary">mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text class="py-5">
      <div v-if="!hasTranslations" class="text-center pa-8">
        <v-icon size="64" color="grey lighten-1">mdi-translate-off</v-icon>
        <p class="text-h6 mt-4 grey--text">
          {{ $t('No translations available') }}
        </p>
        <p class="text-body-2 grey--text">
          {{ $t('Translations will appear here once they are generated') }}
        </p>
      </div>
      <div v-else>
        <div class="d-flex align-center mb-4">
          <v-select
            v-model="selectedField"
            :items="fieldItems"
            label="Select Content Field"
            outlined
            dense
            class="field-select"
            hide-details
          >
            <template #selection="{ item }">
              <div class="d-flex align-center">
                <v-icon v-if="item.isBlank" small color="warning" class="mr-2">
                  mdi-alert-circle-outline
                </v-icon>
                <span>{{ item.text }}</span>
              </div>
            </template>
            <template #item="{ item }">
              <div class="d-flex align-center">
                <v-icon v-if="item.isBlank" small color="warning" class="mr-2">
                  mdi-alert-circle-outline
                </v-icon>
                <span>{{ item.text }}</span>
              </div>
            </template>
          </v-select>
        </div>
        <v-card v-if="selectedField" flat class="pa-4">
          <div class="mb-4">
            <div class="text-h6 font-weight-medium mb-2">
              {{ getFieldLabel(selectedField) }}
            </div>
            <div class="text-caption grey--text mb-3">
              {{ $t('Original (English)') }}
            </div>
            <div class="translation-text original-content">
              {{ originalContent[selectedField] || '-' }}
            </div>
          </div>
          <v-divider class="my-4" />
          <div class="text-subtitle-1 font-weight-medium mb-3">
            {{ $t('Translations') }}
          </div>
          <v-expansion-panels v-model="openPanels" multiple>
            <v-expansion-panel
              v-for="locale in availableLocalesForSelectedField"
              :key="locale"
            >
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <country-flag
                    :country="languageCountries[locale]"
                    :rounded="true"
                    size="small"
                    class="mr-2"
                  />
                  <span class="text-body-1 font-weight-medium">
                    {{ languageNames[locale] }}
                  </span>
                  <v-chip
                    v-if="translationStatus[locale]?.[selectedField]"
                    x-small
                    color="success"
                    class="ml-2"
                  >
                    {{ $t('Translated') }}
                  </v-chip>
                  <v-chip v-else x-small color="grey" class="ml-2">
                    {{ $t('Not translated') }}
                  </v-chip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="pt-2">
                  <div v-if="!isEditing(locale)" class="translation-text">
                    {{
                      translations[locale]?.[selectedField] ||
                      $t('No translation available for this field')
                    }}
                    <div class="d-flex justify-end mt-2">
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="startEdit(locale)"
                      >
                        <v-icon small left>
                          {{
                            translations[locale]?.[selectedField]
                              ? 'mdi-pencil'
                              : 'mdi-plus'
                          }}
                        </v-icon>
                        {{
                          translations[locale]?.[selectedField]
                            ? $t('Edit')
                            : $t('Add Translation')
                        }}
                      </v-btn>
                    </div>
                  </div>
                  <div v-else>
                    <v-textarea
                      v-model="editingText[locale]"
                      outlined
                      dense
                      rows="8"
                      hide-details
                      :placeholder="$t('Enter translation text')"
                    />
                    <div class="d-flex justify-end mt-2">
                      <v-btn
                        small
                        text
                        color="grey"
                        class="mr-2"
                        @click="cancelEdit(locale)"
                      >
                        {{ $t('Cancel') }}
                      </v-btn>
                      <v-btn
                        small
                        color="primary"
                        :loading="saving[locale]"
                        @click="saveTranslation(locale)"
                      >
                        <v-icon small left>mdi-content-save</v-icon>
                        {{ $t('Save') }}
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <div
            v-if="availableLocalesForSelectedField.length === 0"
            class="text-center pa-4 grey--text"
          >
            {{ $t('No translations available for this field') }}
          </div>
        </v-card>
      </div>
    </v-card-text>
    <v-divider />
    <v-card-actions class="justify-space-around">
      <v-col>
        <v-btn depressed @click="onClose">{{ $t('Close') }}</v-btn>
      </v-col>
    </v-card-actions>
  </v-card>
</template>

<script>
import CountryFlag from 'vue-country-flag'
import { LANGUAGES } from '@/consts'
import axios from '@/axios/config'
import Toaster from '@/utils/toaster'

const LANGUAGE_MAP = LANGUAGES.reduce((acc, lang) => {
  acc[lang.lang] = lang
  return acc
}, {})

export default {
  name: 'MarketingContentTranslationsModal',
  components: {
    CountryFlag,
  },
  props: {
    listing: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedField: null,
      openPanels: [],
      editingLocales: {},
      editingText: {},
      saving: {},
      marketingFields: [
        { key: 'summary', label: 'Summary' },
        { key: 'space', label: 'Space' },
        { key: 'access', label: 'Access' },
        { key: 'notes', label: 'Notes' },
        { key: 'houseRules', label: 'House Rules' },
        { key: 'interactionWithGuests', label: 'Interaction' },
        { key: 'neighborhood', label: 'Neighborhood' },
        { key: 'transit', label: 'Transit' },
        { key: 'directions', label: 'Directions' },
      ],
      supportedLocales: [
        'ar',
        'de',
        'es',
        'fr',
        'he',
        'it',
        'ru',
        'sv',
        'th',
        'zh',
        'pl',
      ],
    }
  },
  computed: {
    localization() {
      return this.listing?.localization || {}
    },
    availableLocales() {
      const locales = new Set()

      // Find all locales that have translations for any marketing content field
      this.marketingFields.forEach(field => {
        const fieldKey = `marketing_content.${field.key}`
        const translations = this.localization[fieldKey]

        if (translations && typeof translations === 'object') {
          Object.keys(translations).forEach(locale => {
            if (translations[locale] && translations[locale].trim()) {
              locales.add(locale)
            }
          })
        }
      })

      // Sort locales based on supported locales order, then alphabetically
      return Array.from(locales).sort((a, b) => {
        const indexA = this.supportedLocales.indexOf(a)
        const indexB = this.supportedLocales.indexOf(b)

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        return a.localeCompare(b)
      })
    },
    hasTranslations() {
      return this.availableLocales.length > 0
    },
    translations() {
      const result = {}
      this.availableLocales.forEach(locale => {
        result[locale] = {}
        this.marketingFields.forEach(field => {
          const fieldKey = `marketing_content.${field.key}`
          const fieldTranslations = this.localization[fieldKey]
          result[locale][field.key] = fieldTranslations?.[locale] || ''
        })
      })
      return result
    },
    translationStatus() {
      const result = {}
      this.availableLocales.forEach(locale => {
        result[locale] = {}
        this.marketingFields.forEach(field => {
          const translation = this.translations[locale]?.[field.key]
          result[locale][field.key] =
            translation && translation.trim().length > 0
        })
      })
      return result
    },
    originalContent() {
      const result = {}
      this.marketingFields.forEach(field => {
        result[field.key] = this.listing?.marketing_content?.[field.key] || ''
      })
      return result
    },
    languageNames() {
      const result = {}
      this.availableLocales.forEach(locale => {
        const language = LANGUAGE_MAP[locale]
        result[locale] = language ? language.label : locale.toUpperCase()
      })
      return result
    },
    languageCountries() {
      const result = {}
      this.availableLocales.forEach(locale => {
        const language = LANGUAGE_MAP[locale]
        result[locale] = language ? language.country : 'us'
      })
      return result
    },
    // Format fields for dropdown with translation counts
    fieldItems() {
      return this.marketingFields.map(field => {
        const translatedCount = this.fieldTranslationCounts[field.key] || 0
        const totalLocales = this.availableLocales.length
        const originalContent = this.originalContent[field.key] || ''
        const isOriginalBlank =
          !originalContent || originalContent.trim() === ''

        let countText = ''
        if (totalLocales > 0) {
          countText = ` (${translatedCount}/${totalLocales} ${this.$t(
            'translated'
          )})`
        }

        return {
          value: field.key,
          text: `${field.label}${countText}`,
          isBlank: isOriginalBlank,
        }
      })
    },
    fieldTranslationCounts() {
      const counts = {}
      this.marketingFields.forEach(field => {
        let count = 0
        this.availableLocales.forEach(locale => {
          if (this.translationStatus[locale]?.[field.key]) {
            count++
          }
        })
        counts[field.key] = count
      })
      return counts
    },
    // Get count of translated locales for a field
    getTranslatedCountForField() {
      const cache = {}
      return fieldKey => {
        if (cache[fieldKey] !== undefined) {
          return cache[fieldKey]
        }
        let count = 0
        this.availableLocales.forEach(locale => {
          if (this.translationStatus[locale]?.[fieldKey]) {
            count++
          }
        })
        cache[fieldKey] = count
        return count
      }
    },
    // Get available locales for selected field (includes all supported locales)
    availableLocalesForSelectedField() {
      if (!this.selectedField) return []

      const localesWithTranslations = new Set()
      const fieldKeyPath = `marketing_content.${this.selectedField}`
      const fieldTranslations = this.localization[fieldKeyPath]

      if (fieldTranslations && typeof fieldTranslations === 'object') {
        Object.keys(fieldTranslations).forEach(locale => {
          if (fieldTranslations[locale] && fieldTranslations[locale].trim()) {
            localesWithTranslations.add(locale)
          }
        })
      }

      // Combine locales with translations and all supported locales
      const allLocales = new Set([
        ...localesWithTranslations,
        ...this.supportedLocales,
      ])

      return Array.from(allLocales).sort((a, b) => {
        const indexA = this.supportedLocales.indexOf(a)
        const indexB = this.supportedLocales.indexOf(b)

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        return a.localeCompare(b)
      })
    },
  },
  mounted() {
    if (this.marketingFields.length > 0) {
      this.selectedField = this.marketingFields[0].key
    }
  },
  methods: {
    onClose() {
      this.$store.commit('hideModal')
    },
    // Get field label by key
    getFieldLabel(fieldKey) {
      const field = this.marketingFields.find(f => f.key === fieldKey)
      return field ? field.label : fieldKey
    },
    // Check if locale is being edited
    isEditing(locale) {
      return this.editingLocales[locale] === true
    },
    // Start editing a locale
    startEdit(locale) {
      this.$set(this.editingLocales, locale, true)
      this.$set(
        this.editingText,
        locale,
        this.translations[locale]?.[this.selectedField] || ''
      )
    },
    // Cancel editing
    cancelEdit(locale) {
      this.$set(this.editingLocales, locale, false)
      this.$set(this.editingText, locale, '')
    },
    // Save translation
    async saveTranslation(locale) {
      this.$set(this.saving, locale, true)
      try {
        const response = await axios.patch(
          `/api/listings/${this.listing.id}/update-translation`,
          {
            field: this.selectedField,
            locale: locale,
            translation: this.editingText[locale],
          }
        )

        const fieldKey = `marketing_content.${this.selectedField}`
        if (!this.listing.localization) {
          this.$set(this.listing, 'localization', {})
        }
        if (!this.listing.localization[fieldKey]) {
          this.$set(this.listing.localization, fieldKey, {})
        }
        this.$set(
          this.listing.localization[fieldKey],
          locale,
          this.editingText[locale]
        )

        // Force reactivity update
        this.$forceUpdate()

        this.$set(this.editingLocales, locale, false)
        this.$set(this.editingText, locale, '')
      } catch (error) {
        console.error('Error saving translation:', error)
        Toaster.show([
          {
            text:
              error.response?.data?.error ||
              this.$t('Error saving translation'),
            type: 'error',
          },
        ])
      } finally {
        this.$set(this.saving, locale, false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.original-content {
  background-color: var(--v-light100-base) !important;
}

.translation-text {
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  min-height: 80px;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.5;
  background-color: white;
}

:deep() .v-expansion-panel-header {
  padding: 12px 16px;
}

:deep() .v-expansion-panel-content__wrap {
  padding: 16px;
}

:deep() .v-expansion-panel-content {
  contain: layout style paint;
}

:deep() .v-card {
  contain: layout style paint;
}

.field-select {
  max-width: 300px;
}
</style>
